/**
 * RFC 0345 3c/3d: a document the owner shared into a session opens for its
 * guest. The guest cannot read the owner's project, so its catalog cannot
 * name the document; it is filed under the guest's General, the folder for
 * things shared without a project around them, like a document shared alone.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  addProjectToWorld,
  type DocumentDoc,
  type ProjectDoc,
  type SessionDoc,
  type WorldDoc,
} from '@mod/core/model'
import { useEffect, useMemo, useRef } from 'react'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useDocument, useDocuments } from '../world/repo/useDocument'
import { type OpenSharingAccount, useSharingAccount } from './accountContext'

type Sharing = NonNullable<ReturnType<typeof useTabRepoBoot>['sharing']>

export interface GeneralEntry {
  documentId: string
  title: string
  path: string
  mime: string
}

/** The catalog row for a document read off its own doc. */
export function generalEntry(
  documentId: string,
  doc: Pick<DocumentDoc, 'path' | 'mime'>,
): GeneralEntry {
  const path = String(doc.path || `${documentId}.md`)
  const title = (path.split('/').pop() ?? path).replace(/\.[^.]+$/, '') || path
  return { documentId, title, path, mime: String(doc.mime || 'text/markdown') }
}

/** The General project, made if this person has none yet. */
async function generalProject(
  sharing: Sharing,
  opened: OpenSharingAccount['opened'],
) {
  const world = opened.world
  let generalId = world.doc().sharedDocumentsProjectId
  if (!generalId) {
    const { createSharingProject } = await import('@mod/core/sharing')
    const { project } = await createSharingProject(sharing, opened.person, {
      title: 'General',
    })
    generalId = project.documentId
    world.change((doc) => {
      doc.sharedDocumentsProjectId = generalId
    })
    await addProjectToWorld(sharing.repo, world.documentId, generalId, {
      displayName: 'General',
    })
  }
  return sharing.repo.find<ProjectDoc>(generalId as AnyDocumentId)
}

export async function fileUnderGeneral(
  sharing: Sharing,
  opened: OpenSharingAccount['opened'],
  entries: GeneralEntry[],
): Promise<void> {
  const general = await generalProject(sharing, opened)
  general.change((doc) => {
    doc.documents ??= {}
    for (const e of entries) {
      if (doc.documents[e.documentId]) continue
      doc.documents[e.documentId] = {
        title: e.title,
        path: e.path,
        mime: e.mime,
        updatedAt: Date.now(),
      }
    }
  })
  await sharing.repo.flush()
}

/** In a guest's window: file every document shared into this session. */
export function useGuestSessionDocuments(
  sessionId: string,
  session: SessionDoc | undefined,
): void {
  const account = useSharingAccount()
  const { sharing } = useTabRepoBoot()
  const [world] = useDocument<WorldDoc>(
    account?.opened.location.worldId as AnyDocumentId | undefined,
  )
  const guest = String(world?.sharedWithMe?.[sessionId]?.kind) === 'session'
  const shared = (guest ? (session?.sharedDocumentIds ?? []) : []).map(String)
  const key = shared.join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by contents
  const ids = useMemo(() => shared as AnyDocumentId[], [key])
  const docs = useDocuments<DocumentDoc>(ids)
  const generalId = world?.sharedDocumentsProjectId
  const [general] = useDocument<ProjectDoc>(
    generalId as AnyDocumentId | undefined,
  )
  const filing = useRef(false)
  useEffect(() => {
    if (!account || !sharing || filing.current) return
    // Absent is not empty: wait for General to load before deciding.
    if (generalId && !general) return
    const missing = [...docs]
      .filter(([id]) => !general?.documents?.[id])
      .map(([id, doc]) => generalEntry(id, doc))
    if (!missing.length) return
    filing.current = true
    void fileUnderGeneral(sharing, account.opened, missing)
      .catch((e) => console.warn('[sharing] filing a shared document', e))
      .finally(() => {
        filing.current = false
      })
  }, [account, sharing, docs, general, generalId])
}
